import React, { useEffect, useRef, useState } from "react";

export default function EmptyStateBounce() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const [paused, setPaused] = useState(false);

  const card = useRef({ x: 100, y: 60, w: 90, h: 58, dx: 2.2, dy: 1.8, hue: 48 });

  // Size the backing store to the *CSS* size (clientWidth/Height), not by writing style sizes.
  const fitCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.max(window.devicePixelRatio || 1, 1);

    // Read the CSS box; height is fixed in CSS (prevents feedback loop)
    const cssW = canvas.clientWidth || 720;
    const cssH = canvas.clientHeight || 200;

    // Only set the backing store size; do not touch style.width/height
    canvas.width = Math.max(1, Math.round(cssW * dpr));
    canvas.height = Math.max(1, Math.round(cssH * dpr));

    const ctx = canvas.getContext("2d");
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  useEffect(() => {
    fitCanvas();
    const onResize = () => fitCanvas();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const drawCard = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, hue: number) => {
    ctx.fillStyle = `hsl(${hue}, 95%, 60%)`;
    ctx.strokeStyle = "rgba(0,0,0,0.15)";
    ctx.lineWidth = 2;
    const r = 8;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    const fold = Math.min(14, Math.min(w, h) * 0.25);
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.beginPath();
    ctx.moveTo(x + w, y);
    ctx.lineTo(x + w - fold, y);
    ctx.lineTo(x + w, y + fold);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = "rgba(0,0,0,0.25)";
    ctx.lineWidth = 1.5;
    const pad = 10;
    const lineGap = 10;
    for (let i = 0; i < 3; i++) {
      const ly = y + pad + 10 + i * lineGap;
      ctx.beginPath();
      ctx.moveTo(x + pad, ly);
      ctx.lineTo(x + w - pad, ly);
      ctx.stroke();
    }
  };

  const step = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Read the current CSS size (stable because height is fixed in CSS)
    const cssW = canvas.clientWidth || 720;
    const cssH = canvas.clientHeight || 200;

    ctx.clearRect(0, 0, cssW, cssH);

    // background
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, cssW, cssH);
    ctx.fillStyle = "#f7f7f9";
    ctx.fillRect(0, cssH - 36, cssW, 36);

    if (!paused) {
      card.current.x += card.current.dx;
      card.current.y += card.current.dy;

      if (card.current.x <= 6 || card.current.x + card.current.w >= cssW - 6) {
        card.current.dx *= -1;
        card.current.hue = (card.current.hue + 53) % 360;
      }
      if (card.current.y <= 6 || card.current.y + card.current.h >= cssH - 42) {
        card.current.dy *= -1;
        card.current.hue = (card.current.hue + 67) % 360;
      }
    }

    drawCard(ctx, card.current.x, card.current.y, card.current.w, card.current.h, card.current.hue);

    ctx.fillStyle = "#667085";
    ctx.font = "12px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial";
    ctx.fillText(paused ? "Paused — click or press Space to resume" : "Click or press Space to pause", 12, cssH - 14);

    rafRef.current = requestAnimationFrame(step);
  };

  useEffect(() => {
    fitCanvas();
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [paused]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code === "Space") { e.preventDefault(); setPaused(p => !p); }
    };
    const onClick = () => setPaused(p => !p);
    const c = canvasRef.current;
    window.addEventListener("keydown", onKey);
    c?.addEventListener("click", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      c?.removeEventListener("click", onClick);
    };
  }, []);

  return (
    <div className="empty-fun card">
      <div className="muted" style={{ marginBottom: 6 }}>
        No CueBoards yet. Add one, then add the General Starter Pack to round out universal questions.
      </div>
      <canvas ref={canvasRef} aria-label="Bouncing Cue Card mini-game" />
    </div>
  );
}
